
/* System defined core library */
import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, Subject } from 'rxjs';
import { Location } from '@angular/common';

/* User defined library/components */
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { LocationSelector } from '../../../shared/models/scheduling/locationSelector.model';
import { State } from '../../../shared/models/scheduling/state.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { MetricDetails, MetricData } from '../../../shared/models/scheduling/metrics.model';

/* User defined enums/constants */
import { ComponentType, MetricType } from '../../../shared/enums/scheduling.enum';
import { RoutingService } from '../../../shared/services/routing.service';
import { BootstrapParams } from '../../../../bootstrap-params';
import { LocationSelectorComponent } from '../../shared/components/location-selector/location-selector.component';

@Component({
    selector: 'app-schedule-location',
    templateUrl: './schedule-location.component.html'
})

export class ScheduleLocationComponent implements OnInit {

    public isBackToBackRequired: boolean = false;
    public isCurrentFormChanged: boolean;
    public typeaheadNoResults: boolean = false;
    public region: State;
    public selectedStartDate: any;
    public selectedEndDate: any;
    public selectedIndex: number = 0;
    public selectedAppointments: AppointmentDetail[] = [];
    public isLocationSelected: boolean = false;
    public startDateError: string = '';
    public endDateError: string = '';
    public examName: string;
    public client: Client;
    public exam: Exam;
    public program: Program;
    public metricDetails: MetricDetails;
    public firstTimeSearch: boolean;
    public isGoogleMapValid: boolean = true;
    public selectedCountry: Country;
    public selectedState: State;
    public showCandidateSelectableView: boolean = false;
    public userCaptchaInput: string = '';

    /* Input-OutPut parameters decalartions */
    @Input() SelectedStartDate: any;
    @Input() SelectedEndDate: any;

    /* Service subscriptions */
    region$: Subscription;
    selectedLocation$: Subscription;
    selectedStartDate$: Subscription;
    selectedEndDate$: Subscription;
    selectedAppointments$: Subscription;
    client$: Subscription;
    program$: Subscription;
    exam$: Subscription;
    metricDetails$: Subscription;
    firstTimeSearch$: Subscription;
    country$: Subscription;
    state$: Subscription;

    @ViewChild(LocationSelectorComponent) locationSelectorInstance: LocationSelectorComponent;

    /* Constructor declaration with required injected services */
    constructor(private _location: Location,
        private utilityService: UtilityService,
        private router: Router,
        private selectedExamDetailService: SelectedExamDetailService,
        private routingService: RoutingService,
        private bootstrapParams: BootstrapParams
    ) {

        this.region$ = selectedExamDetailService.region.subscribe(region => {
            this.region = region;
        });

        this.selectedStartDate$ = selectedExamDetailService.selectedStartDate.subscribe(selectedStartDate => {
            this.selectedStartDate = selectedStartDate;
        });

        this.selectedEndDate$ = selectedExamDetailService.selectedEndDate.subscribe(selectedEndDate => {
            this.selectedEndDate = selectedEndDate;
        });

        this.client$ = selectedExamDetailService.client.subscribe(
            client => {
                this.client = client;
            });
        this.program$ = selectedExamDetailService.program.subscribe(program => {
            this.program = program;
        });

        this.exam$ = selectedExamDetailService.exam.subscribe(exam => {
            this.exam = exam;
        });

        this.country$ = selectedExamDetailService.country.subscribe(country => {
            this.selectedCountry = country;
        });

        this.state$ = selectedExamDetailService.state.subscribe(state => {
            this.selectedState = state;
        });

        this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

        this.metricDetails$ = selectedExamDetailService.metricDetails.subscribe(metricDetails => {
            this.metricDetails = metricDetails;
        });

        this.firstTimeSearch$ = this.selectedExamDetailService.firstTimeSearch.subscribe(firstTimeSearch => {
            this.firstTimeSearch = firstTimeSearch;
        });

        this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
            selectedAppointments => {
                this.selectedAppointments = selectedAppointments;
            });

        this.showCandidateSelectableView = this.selectedExamDetailService.showCandidateSelectableView;
        this.isGoogleMapValid = this.selectedExamDetailService.isGoogleMapsValid;

        if (this.region != null && this.region != undefined && this.isGoogleMapValid) {
            this.isLocationSelected = true;
        }

        if (!this.isGoogleMapValid) {
            this.validateRegionSelector();
        }

        this.selectedExamDetailService.getUserCaptchaInput().subscribe(captcha => {
            this.userCaptchaInput = captcha;
        });
    }

    /* Initialize the component & call require methods when component loaded first time. */
    ngOnInit() {
        this.isCurrentFormChanged = false;
        sessionStorage.setItem('previousUrl', window.location.pathname);
        if (!this.utilityService.isDataValidToLoadComponent(ComponentType.ScheduleLocationComponent)) return;

        if (this.selectedIndex > -1) { this.examName = this.selectedAppointments[this.selectedIndex].exam.name; }

        if (this.selectedAppointments == null || this.selectedAppointments == undefined) {
            this.selectedAppointments = [];
            let tmpAppointmentDetail: AppointmentDetail = new AppointmentDetail();
            tmpAppointmentDetail.client = this.client;
            tmpAppointmentDetail.program = this.program;
            tmpAppointmentDetail.exam = this.exam;
            this.selectedAppointments.push(tmpAppointmentDetail);
            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
        }
    }

    /* Use to move back to previous screen */
    goBack(): void {
        if (!this.isCurrentFormChanged) {
            this.selectedExamDetailService.setSelectedLocation(null);
        }

        let seatval: LocationSelector = {
            region: this.region, selectedStartDate: this.selectedStartDate, selectedEndDate: this.selectedEndDate
        };
        this.selectedExamDetailService.setSelectedLocation(seatval);

        this.selectedAppointments[this.selectedIndex].region = (this.region != null && this.region != undefined) ?
            this.region : this.selectedAppointments[this.selectedIndex].region;
        this.routingService.scheduleBwdNavigation(ComponentType.ScheduleLocationComponent);
    }

    /* Use to move next screen */
    submit(event: any): void {
        if(this.bootstrapParams.captchaEnable && this.locationSelectorInstance){
            this.locationSelectorInstance.validateCaptcha();
            this.selectedExamDetailService.getCaptchaValidationResult().subscribe(isCaptchaValid => {
                if (isCaptchaValid) {
                    this.proceedToNextPage();
                }
            });
        }
        else{
            this.proceedToNextPage();
        }
    }

    proceedToNextPage(){
        let seatval: LocationSelector = { region: this.region, selectedStartDate: this.selectedStartDate, selectedEndDate: this.selectedEndDate };
        this.selectedExamDetailService.setSelectedLocation(seatval);

        if (this.selectedIndex > -1) {
            this.selectedAppointments[this.selectedIndex].region = this.region;
            this.selectedAppointments[this.selectedIndex].selectedStartDate = this.selectedStartDate;
            this.selectedAppointments[this.selectedIndex].selectedEndDate = this.selectedEndDate;
            this.selectedAppointments[this.selectedIndex].appointmentSlot = new AppointmentSlot();
            this.selectedAppointments[this.selectedIndex].hold = new Hold();
        }
        this.selectedExamDetailService.setSlot(null);// Set previous slot NULL.
        this.selectedExamDetailService.setHold(null);// Set previous Hold NULL.
        this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
        this.selectedExamDetailService.selectedAppointmentIndex = 0;

        this.routingService.scheduleFwdNavigation(ComponentType.ScheduleLocationComponent);
    }

    public isDisable(): boolean {
        return (
            !this.isLocationSelected
            || this.selectedStartDate == null
            || (this.endDateError != '')
            || (this.startDateError != '')
            || (this.userCaptchaInput == '' && this.bootstrapParams.captchaEnable)
            || (this.isGoogleMapValid && ((this.typeaheadNoResults) || (this.region == null))))
    }

    public getErrorMessage(event: any): void {
        this.endDateError = event.endDateError;
        this.startDateError = event.startDateError;
        this.typeaheadNoResults = event.typeaheadNoResults;
    }

    public getSelectedLocation(event: any): void {
        this.isLocationSelected = event.isLocationSelected;
        this.isCurrentFormChanged = event.isCurrentFormChanged;

    }


    /* Validate Region selector is valid or not */
    validateRegionSelector() {
        if (this.selectedCountry == null || this.selectedCountry == undefined) {
            this.isLocationSelected = false;
        }
        else if ((this.selectedCountry != null || this.selectedCountry != undefined) &&
            (this.selectedState != null && this.selectedState != undefined && this.selectedState.id == "-1")) {
            this.isLocationSelected = true;
        }
        else if ((this.selectedCountry != null || this.selectedCountry != undefined) &&
            (this.selectedState != null && this.selectedState != undefined && this.selectedState.id != "-1")) {
            this.isLocationSelected = true;
        }
        else {
            this.isLocationSelected = false;
        }
    }

    validRegionSelector(event: any) {
        this.isLocationSelected = event;
    }

    /* Set MetricDetails in shared service */
    setMetricDetails(): void {
        this.metricDetails = new MetricDetails();
        if (this.region) {
            this.metricDetails.searched = new MetricData(
                MetricType.Searched,
                { "latitude": this.region.lat, "longitude": this.region.long },
                this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), "-")
            );
            if (!this.firstTimeSearch) {
                this.selectedExamDetailService.setFirstTimeSearch(true);
                this.selectedExamDetailService.setMetricDetails(this.metricDetails);
            } else {
                this.selectedExamDetailService.setFirstTimeSearch(false);
            }
        }

    }
}